import { gql } from '@apollo/client';

/** Bulk delete proposals in one request (better performance than N single deletes). */
export const BULK_DELETE_PROPOSALS_MUTATION = gql`
  mutation BulkDeleteProposalsMutation($topic: String!, $pidList: [String!]!, $remove: Boolean) {
    bulkDeleteProposals(topic: $topic, pidList: $pidList, remove: $remove) {
      deletedIds
      failedIds
    }
  }
`;

/** Bulk update proposals in one request (approve, decline, tags, restore, etc.). */
export const BULK_UPDATE_PROPOSALS_MUTATION = gql`
  mutation BulkUpdateProposalsMutation(
    $topic: String!
    $updates: [BulkProposalUpdateItemInput!]!
    $origin: String
  ) {
    bulkUpdateProposals(topic: $topic, updates: $updates, origin: $origin) {
      updated {
        _id
        pid
        uid
        auid
        name
        state
        tags
      }
      failedIds
    }
  }
`;

export const UPDATE_PROPOSAL_MUTATION = gql`
  mutation updateProposalMutation(
    $proposal: ProposalInput!
    $updateType: String!
    $topic: String!
  ) {
    updateProposal(topic: $topic, updateType: $updateType, proposal: $proposal) {
      _id
      pid
      uid
      auid
      name
      state
      draft
      email
      channel
      client {
        contact
        name
        desc
        address
        text
        email
      }
      seenTime
      seenCount
      lastSeen
      emailLogs {
        date
        recipient
        type
        event
      }
      openLogs {
        date
        clientIP
        duration
        watched
        pageTime
        deviceInfo {
          deviceType
          deviceOS
          deviceBrowser
          deviceName
          deviceAgent
        }
      }
      date
      project {
        name
        type
        othertype
        name
        desc
        goals
      }
      version
      sawyou
      pnum
      pricing
      deliverables
      milestones
      sawmilestones
      superPowers
      curr
      priceSeperator
      decimalPrecision
      language
      expiryDate
      expiryMessage
      currency
      callButton
      contactButton
      contactnumber
      enablePayment
      clientPayableAmount
      clientPayableLabel
      clientPayableCurrency
      clientPayableLink
      userway
      scripts
      chatway
      chatwayScripts
      stripe {
        stripe_user_id
        isActive
        currency
        country
        isSquareConnected
        type
      }
      payment {
        status
        amount
        currency
        payment_intend
        created
      }
      published
      disableSig
      signatureEmails
      selectedOption
      prevState
      payedfor
      redirectTo
      redirectAfter
      proposalRedirectAfter
      proposalRedirectTo
      sigOptions
      contactOptions {
        option
        value
        enabled
      }
      sigtype
      signature {
        name
        myname
        additionalSignature
        signatureBackgroundColor
        signatureTextColor
        additionalSignatureBackgroundColor
        additionalSignatureTextColor
        email
        date
        clientIP
        device
      }
      attachments {
        name
        type
        url
        size
      }
      isEditing {
        name
        uid
        tabID
      }
      wixConnectedInstance
      importedProposal
      fromUserTemplate
      spamUrls {
        url
        blocked
      }
      wixProposalId
      isBasicTemplate
      wixPricing {
        lineItems {
          quantity
          name
          description
          price
          pricesBreakdown {
            totalPriceAfterTax
            taxDetails {
              taxRate
              taxableAmount
              taxable
              totalTax
              taxName
              rateBreakdown {
                rate
                taxableAmount
                tax
              }
            }
            totalDiscount
            price
            priceBeforeDiscounts
            lineItemPrice
            fullPrice
          }
          itemType {
            preset
          }
        }
        appliedDiscounts {
          discountType
          lineItemIds
        }
        additionalFees {
          name
          priceBeforeTax
          taxDetails {
            taxRate
            taxableAmount
            taxable
            totalTax
            taxName
            rateBreakdown {
              rate
              taxableAmount
              tax
            }
          }
          price
        }
        calculatedTotals {
          priceSummary {
            total
            subtotal
            discount
            additionalFees
          }
        }
        paymentSettings {
          type
          singlePaymentSchedule {
            dueDate
            dueDateOffset
            issueDate
            deposit {
              percentage
              amount
            }
            paymentStatus
            amountPaid
          }
          multiplePaymentSchedule {
            items {
              dueDate
              dueDateOffset
              issueDate
              deposit {
                percentage
                amount
              }
              issueDateOffset
              title
              percentage
              amount
              paymentStatus
              amountPaid
            }
          }
        }
        currencyCode
        locale {
          country
          language
        }
      }
      sharedViaMail
      isScheduled
      lastStatusUpdateTime
      emailSettings {
        scheduled {
          status
          utcTime
          scheduleTime
          timezone
        }
        publishedProposalToProspect {
          status
          sent
          date
          reminder
          customize {
            subjectLine {
              RTL
              value
            }
            previewText {
              RTL
              value
            }
            emailBody {
              RTL
              value
            }
            buttonText {
              RTL
              value
            }
            reminder
          }
        }
        reminderToProspect {
          status
          sent
          date
          reminder
          customize {
            subjectLine {
              RTL
              value
            }
            previewText {
              RTL
              value
            }
            emailBody {
              RTL
              value
            }
            buttonText {
              RTL
              value
            }
            reminder
          }
        }
        secondReminderToProspect {
          status
          sent
          date
          reminder
          customize {
            subjectLine {
              RTL
              value
            }
            previewText {
              RTL
              value
            }
            emailBody {
              RTL
              value
            }
            buttonText {
              RTL
              value
            }
            reminder
          }
        }
        signedPDFToProspect {
          status
          sent
          date
          reminder
          customize {
            subjectLine {
              RTL
              value
            }
            previewText {
              RTL
              value
            }
            emailBody {
              RTL
              value
            }
            buttonText {
              RTL
              value
            }
            reminder
          }
        }
        recipients {
          firstName
          lastName
          email
        }
      }
      isWixTemplate
      tags
      hubspot {
        hubspotObjectType
        hubspotContactId
        hubspotDealId
        hubspotCompanyId
      }
    }
  }
`;

/** Saves only the changed paths of a proposal (editor saves). */
export const UPDATE_PARTIAL_PROPOSAL_MUTATION = gql`
  mutation updatePartialProposalMutation(
    $topic: String!
    $proposal: PartialProposalInput!
    $updateType: String
  ) {
    updatePartialProposal(topic: $topic, proposal: $proposal, updateType: $updateType) {
      _id
      pid
      uid
      auid
      state
      prevState
      edited
      editEvent
      draftRev
      published
      approvedmanually
      disableSig
      isEditing {
        name
        uid
        tabID
      }
      signature {
        name
        myname
        additionalSignature
        signatureBackgroundColor
        signatureTextColor
        additionalSignatureBackgroundColor
        additionalSignatureTextColor
        email
        date
        clientIP
        device
      }
      draft
    }
  }
`;

// Records a prospect's viewing time with one session entry; replies with only openLogs and lastSeen
export const TRACK_PROPOSAL_TIME_MUTATION = gql`
  mutation trackProposalTimeMutation(
    $proposalId: String!
    $session: OpenLogsInput!
    $newSession: Boolean
    $viewToken: String
  ) {
    trackProposalTime(
      proposalId: $proposalId
      session: $session
      newSession: $newSession
      viewToken: $viewToken
    ) {
      _id
      lastSeen
      openLogs {
        date
        clientIP
        duration
        watched
        pageTime
        deviceInfo {
          deviceType
          deviceOS
          deviceBrowser
          deviceName
          deviceAgent
        }
      }
    }
  }
`;

export const UPDATE_PROPOSAL_CLIENT_MUTATION = gql`
  mutation updateProposalClientMutation(
    $proposal: ProposalClientInput!
    $updateType: String!
    $topic: String!
    $origin: String
    $viewToken: String
  ) {
    updateProposalClient(
      topic: $topic
      updateType: $updateType
      proposal: $proposal
      origin: $origin
      viewToken: $viewToken
    ) {
      _id
      pid
      uid
      auid
      name
      state
      channel
      draft
      email
      pnum
      isSecured
      client {
        contact
        name
        desc
        address
        text
        email
      }
      seenTime
      seenCount
      lastSeen
      emailLogs {
        date
        recipient
        type
        event
      }
      openLogs {
        date
        clientIP
        duration
        watched
        pageTime
        deviceInfo {
          deviceType
          deviceOS
          deviceBrowser
          deviceName
          deviceAgent
        }
      }
      date
      project {
        name
        type
        othertype
        name
        desc
        goals
      }
      version
      sawyou
      pricing
      deliverables
      editEvent
      enableAITool
      milestones
      sawmilestones
      superPowers
      curr
      priceSeperator
      decimalPrecision
      language
      expiryDate
      expiryMessage
      currency
      callButton
      contactButton
      contactnumber
      enablePayment
      clientPayableAmount
      clientPayableLabel
      clientPayableCurrency
      clientPayableLink
      scripts
      chatwayScripts
      stripe {
        stripe_user_id
        isActive
        currency
        country
        isSquareConnected
        type
      }
      payment {
        status
        amount
        currency
        payment_intend
        created
      }
      approvedmanually
      published
      disableSig
      signatureEmails
      selectedOption
      prevState
      payedfor
      redirectTo
      redirectAfter
      proposalRedirectTo
      proposalRedirectAfter
      sigOptions
      lockedData
      contactOptions {
        option
        value
        enabled
      }
      sigtype
      signature {
        name
        myname
        additionalSignature
        signatureBackgroundColor
        signatureTextColor
        additionalSignatureBackgroundColor
        additionalSignatureTextColor
        email
        date
        clientIP
        device
      }
      attachments {
        name
        type
        url
        size
      }
      isEditing {
        name
        uid
        tabID
      }
      fromUserTemplate
      importedProposal
      spamUrls {
        url
        blocked
      }
      isBasicTemplate
      spamUrls {
        url
        blocked
      }
      wixProposalId
      wixPricing {
        lineItems {
          quantity
          name
          description
          price
          pricesBreakdown {
            totalPriceAfterTax
            taxDetails {
              taxRate
              taxableAmount
              taxable
              totalTax
              taxName
              rateBreakdown {
                rate
                taxableAmount
                tax
              }
            }
            totalDiscount
            price
            priceBeforeDiscounts
            lineItemPrice
            fullPrice
          }
          itemType {
            preset
          }
        }
        appliedDiscounts {
          discountType
          lineItemIds
        }
        additionalFees {
          name
          priceBeforeTax
          taxDetails {
            taxRate
            taxableAmount
            taxable
            totalTax
            taxName
            rateBreakdown {
              rate
              taxableAmount
              tax
            }
          }
          price
        }
        calculatedTotals {
          priceSummary {
            total
            subtotal
            discount
            additionalFees
          }
        }
        paymentSettings {
          type
          singlePaymentSchedule {
            dueDate
            dueDateOffset
            issueDate
            deposit {
              percentage
              amount
            }
            paymentStatus
            amountPaid
          }
          multiplePaymentSchedule {
            items {
              dueDate
              dueDateOffset
              issueDate
              deposit {
                percentage
                amount
              }
              issueDateOffset
              title
              percentage
              amount
              paymentStatus
              amountPaid
            }
          }
        }
        currencyCode
        locale {
          country
          language
        }
      }
      isWixTemplate
    }
  }
`;

export const CREATE_PROPOSAL_MUTATION = gql`
  mutation createProposal(
    $pid: String!
    $topic: String!
    $duplicate: DuplicateProposalInput
    $template: TemplateProposalInput
    $importedProposal: JSON
    $additionalInfo: AdditionalProposalInput
  ) {
    createProposal(
      pid: $pid
      topic: $topic
      duplicate: $duplicate
      template: $template
      importedProposal: $importedProposal
      additionalInfo: $additionalInfo
    ) {
      _id
      pid
      uid
      cid
      name
      date
      pnum
    }
  }
`;

export const CHECK_VIDEO_OEMBED_MUTATION = gql`
  mutation checkVideoOembedMutation($link: String!) {
    handleOembed(link: $link)
  }
`;

export const ADD_PROPOSAL_ATTACHMENTS = gql`
  mutation addProposalAttachmentMutation(
    $proposalId: String!
    $attachments: [ProposalAttachmentInput!]!
    $topic: String!
    $type: String!
  ) {
    addProposalAttachment(
      topic: $topic
      attachments: $attachments
      proposalId: $proposalId
      type: $type
    ) {
      _id
      attachments {
        name
        type
        url
        size
      }
    }
  }
`;

export const DELETE_PROPOSAL_ATTACHMENT = gql`
  mutation deleteProposalAttachmentMutation(
    $proposalId: String!
    $index: Float!
    $topic: String!
    $type: String!
  ) {
    deleteProposalAttachment(topic: $topic, proposalId: $proposalId, index: $index, type: $type) {
      _id
      attachments {
        name
        type
        url
        size
      }
    }
  }
`;

export const DELETE_PROPOSAL_FILES = gql`
  mutation deleteProposalFiles($urls: [String!]!) {
    deleteProposalFiles(urls: $urls)
  }
`;

export const VERIFY_PROPOSAL_PASSWORD = gql`
  # Write your query or mutation here
  mutation VerifyProposalPassword($pid: String!, $password: String!) {
    verifyProposalPassword(pid: $pid, password: $password) {
      token
    }
  }
`;
